/**
 * DRETable — tabela mensal do DRE.
 */

import Icon from '../icons.jsx'

import {
  formatValue,
  formatVar,
  toneClass,
} from '../../lib/dreFormat.js'

// Cor e direção da variação vêm 100% do backend (campo `direction`). O front
// só mapeia direction → classe/seta, sem recalcular a semântica da cor.
function VarBadge({
  varPct,
  direction,
}) {
  if (varPct == null) {
    return (
      <span className="dre-var dre-var-na">
        —
      </span>
    )
  }

  const tone = toneClass(direction)

  const arrow =
    tone === 'pos'
      ? 'arrow_up'
      : tone === 'neg'
      ? 'arrow_down'
      : 'more'

  return (
    <span className={`dre-var dre-var-${tone}`}>
      <Icon n={arrow} s={10} />

      {formatVar(varPct)}
    </span>
  )
}

function SubHeader({
  full,
  isCurrent,
}) {
  const cls = isCurrent
    ? 'dre-sub is-current'
    : 'dre-sub'

  return (
    <>
      <th className={`${cls} dre-sub-meta`}>
        Meta
      </th>

      {full && (
        <th className={`${cls} dre-sub-proj`}>
          Proj.
        </th>
      )}

      <th className={`${cls} dre-sub-real`}>
        Real.
      </th>

      <th className={`${cls} dre-sub-var`}>
        Var%
      </th>
    </>
  )
}

function MetricCells({
  cell,
  format,
  full,
  isCurrent,
}) {
  const cls = isCurrent ? 'is-current' : ''

  return (
    <>
      <td className={`dre-cell dre-cell-meta ${cls}`}>
        {formatValue(cell.meta, format)}
      </td>

      {full && (
        <td className={`dre-cell dre-cell-proj ${cls}`}>
          {formatValue(cell.projected, format)}
        </td>
      )}

      <td className={`dre-cell dre-cell-real ${cls}`}>
        {formatValue(cell.realized, format)}
      </td>

      <td className={`dre-cell dre-cell-var ${cls}`}>
        <VarBadge
          varPct={cell.varPct}
          direction={cell.direction}
        />
      </td>
    </>
  )
}

function BlockRows({
  block,
  months,
  full,
  totalCols,
}) {
  return (
    <>
      <tr className="dre-block-head">
        <td
          className="dre-block-head-cell dre-sticky"
          colSpan={totalCols}
        >
          <span className="dre-block-icon">
            <Icon n={block.icon} s={13} />
          </span>

          {block.label}
        </td>
      </tr>

      {block.rows.map((row) => (
        <tr
          key={row.key}
          className="dre-row"
        >
          <td className="dre-td-metric dre-sticky">
            {row.label}
          </td>

          {row.cells.map((cell, i) => (
            <MetricCells
              key={months[i]?.key ?? i}
              cell={cell}
              format={row.format}
              full={full}
              isCurrent={months[i]?.isCurrent}
            />
          ))}
        </tr>
      ))}
    </>
  )
}

export default function DRETable({
  months = [],
  blocks = [],
  granularity = 'month',
  onGranularity = () => {},
}) {
  // Coluna PROJ. desativada temporariamente (cálculo está correto, mas não é
  // usada agora). Trocar SHOW_PROJ para `true` reativa Proj. na tabela —
  // SubHeader e MetricCells já renderizam a coluna quando `full` é true.
  const SHOW_PROJ = false
  const full = SHOW_PROJ

  const colsPerMonth = full ? 4 : 3
  const totalCols = months.length * colsPerMonth + 1

  return (
    <section className="dashboard-section">
      <div className="dre-table-wrap">

        {/* Toolbar */}
        <div className="dre-table-toolbar">

          <div className="dre-legend">
            <span className="dre-legend-item">
              <span className="dre-legend-sw meta" />
              Meta
            </span>

            <span className="dre-legend-item">
              <span className="dre-legend-sw proj" />
              Projetado
            </span>

            <span className="dre-legend-item">
              <span className="dre-legend-sw real" />
              Realizado
            </span>

            <span className="dre-legend-item">
              Var % vs período anterior
            </span>
          </div>

          <div className="dre-granularity-toggle">
            <button
              type="button"
              className={granularity === 'week' ? 'active' : ''}
              onClick={() => onGranularity('week')}
            >
              Semanal
            </button>

            <button
              type="button"
              className={granularity === 'month' ? 'active' : ''}
              onClick={() => onGranularity('month')}
            >
              Mensal
            </button>
          </div>
        </div>

        {/* Scroll horizontal */}
        <div className="dre-table-scroll">

          <table className="dre-table">

            <thead>

              {/* Linha dos meses */}
              <tr className="dre-thead-months">

                <th
                  className="dre-th-metric dre-sticky"
                  rowSpan={2}
                >
                  Indicador
                </th>

                {months.map((m) => (
                  <th
                    key={m.key}
                    colSpan={colsPerMonth}
                    className={`dre-th-month ${
                      m.isCurrent ? 'is-current' : ''
                    }`}
                  >
                    <div className="dre-th-month-inner">

                      <span className="dre-th-month-label">
                        {m.label}
                      </span>

                      {m.isCurrent && (
                        <span className="dre-th-month-tag">
                          parcial
                        </span>
                      )}

                      {m.range && (
                        <span className="dre-th-month-range">
                          {m.range}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>

              {/* Linha proj/real/var */}
              <tr className="dre-thead-sub">
                {months.map((m) => (
                  <SubHeader
                    key={m.key}
                    full={full}
                    isCurrent={m.isCurrent}
                  />
                ))}
              </tr>

            </thead>

            <tbody>
              {blocks.map((block) => (
                <BlockRows
                  key={block.id}
                  block={block}
                  months={months}
                  full={full}
                  totalCols={totalCols}
                />
              ))}
            </tbody>

          </table>
        </div>
      </div>
    </section>
  )
}